// src/features/tasks/pages/TaskDetailsPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, CircularProgress, Alert } from '@mui/material';

// Hooks
import { useTasks } from '../hooks/useTasks';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { usePermissions, useTaskPermissions } from '../../auth/hooks/usePermissions';
import { useTaskActions } from '../hooks/useTaskActions';

// Components
import TaskHeader from '../components/TaskDetailsPage/TaskHeader';
import TaskInfoCard, { TaskFacts } from '../components/TaskDetailsPage/TaskInfoCard';
import TaskManualsCard from '../components/TaskDetailsPage/TaskManualsCard';
import TaskCompletionCard from '../components/TaskDetailsPage/TaskCompletionCard';
import GlobalTaskActionsDialog from '../components/GlobalTaskActionsDialog';
import TaskTabs from '../components/TaskDetailsPage/tabs';
import { useTranslation } from 'react-i18next';
import TaskEquipmentCard from '../components/TaskDetailsPage/TaskEquipmentCard';
import { TaskProcedureCard } from '../components/TaskDetailsPage/TaskProcedureCard';
import TaskCompletionReceiptCard from '../components/TaskDetailsPage/TaskCompletionReceiptCard';
import TaskCompletionApprovalCard from '../components/TaskDetailsPage/TaskCompletionApprovalCard';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { getAvailableTransitions } from '../utils/taskStateUtils';

export default function TaskDetailsPage() {
  const navigate = useNavigate();
  const { taskId } = useParams();
  const { showError, showSuccess } = useSnackbar();
  const { t } = useTranslation(["common", "tasks", "files", "equipment", "rooms"]);
  const { navigateBack, navigateSilent } = useNavigationStack();
  const permissions = usePermissions();
  const { canModifyEquipment, canModifyManuals } = useTaskPermissions();

  // Equipment and manuals only get a card once the task has some; until then
  // TaskFacts offers the add buttons. Counts are null until loaded.
  const [equipmentCount, setEquipmentCount] = useState(null);
  const [manualCount, setManualCount] = useState(null);
  const [addEquipmentOpen, setAddEquipmentOpen] = useState(false);
  const [addManualOpen, setAddManualOpen] = useState(false);

  const [ doUpdate, setDoUpdate ] = useState(0);
  // Task id whose fetch has finished: until then a missing task means "not
  // loaded yet", not "not found"
  const [loadedTaskId, setLoadedTaskId] = useState(null);

  // API hooks
  const {
    currentTask,
    loading,
    error,
    clearError,
    fetchTaskById,
    copyTask,
  } = useTasks();

  // Create a ref to always have the current taskId
  const taskIdRef = useRef();
  taskIdRef.current = taskId;

  // Load task data function - use ref to get current taskId
  const loadTaskData = useCallback(async () => {
    const currentTaskId = taskIdRef.current;
    if (!currentTaskId) return;
    setDoUpdate(prev => prev + 1); 
    
    try {
      console.log("Loading task data", currentTaskId);
      await fetchTaskById(currentTaskId);
    } catch (error) {
      showError(t('tasks:errors.load_failed'));
    } finally {
      setLoadedTaskId(currentTaskId);
    }
  }, [setDoUpdate, fetchTaskById, showError]);

  // Custom hook for task actions
  const {
    handleStateChange,
    dialogStates,
    dialogHandlers,
  } = useTaskActions({
    refreshTasks: loadTaskData, // This is stable but uses current taskId via ref
    onSuccess: (data) => {
      console.log('Task action completed successfully:', data);
    }
  });

  // Load task data on mount and when taskId changes
  useEffect(() => {
    if (taskId) {
      loadTaskData();
    }
  }, [taskId, loadTaskData]);

  // Event handlers
  const handleBack = () => navigateBack('/tasks');
  const handleEdit = () => navigateSilent(`/tasks/${taskId}/edit`);
  const handleViewEquipment = (equipment_id) => navigate(`/equipment/${equipment_id}`);
  const handleCopy = useCallback(async () => {
    try {
      const result = await copyTask(taskId);
      if (result.success) {
        const newTaskId = result.data.data.task_id;
        showSuccess(t('tasks:messages.task_copied'));
        navigate(`/tasks/${newTaskId}/edit`);
      } else {
        showError(result.error || t('tasks:messages.copy_failed'));
      }
    } catch {
      showError(t('tasks:messages.copy_failed'));
    }
  }, [copyTask, taskId, navigate, showSuccess, showError, t]);

  // Wrapper for state change to pass the current task
  const handleTaskStateChange = useCallback((action) => {
    if (currentTask) {
      handleStateChange(currentTask, action);
    }
  }, [currentTask, handleStateChange]);

  // A finished procedure is usually the last thing the task needs, so go
  // straight on to completing the task when nothing else blocks it
  const handleProcedureCompleted = useCallback(() => {
    if (!currentTask || !permissions.hasScope('task:state:update')) return;
    if (currentTask.requires_dual_signature && currentTask.completion_receipt_status !== 'COMPLETE') return;
    if (!getAvailableTransitions(currentTask).some(({ action }) => action === 'complete')) return;
    handleStateChange(currentTask, 'complete');
  }, [currentTask, permissions, handleStateChange]);

  // Permission checks
  const canUpdate = permissions.hasScope('task:update');
  const canCreate = permissions.hasScope('task:create');
  const canChangeState = permissions.hasScope('task:state:update');
  const canAddEquipment = canModifyEquipment && !['COMPLETED', 'NOT_COMPLETED'].includes(currentTask?.state);

  // Which content card the equipment column sits beside (see the layout below)
  const hasInfo = Boolean(currentTask?.description?.trim()) || Boolean(currentTask?.send_email);
  const hasReceipt = Boolean(currentTask?.requires_dual_signature);
  const equipmentSide = equipmentCount > 0
    ? (hasReceipt && 'receipt') || (hasInfo && 'info') || (manualCount > 0 && 'manuals') || (currentTask?.procedure && 'procedure') || null
    : null;

  // Grid placement per card. Wide screens: fixed rows, the partner card in
  // column 1 and equipment beside it in column 2; the rest span both columns.
  // Narrow screens: one column, equipment just before its partner.
  const ROWS = { receipt: 1, approval: 2, info: 3, procedure: 4, manuals: 5 };
  const place = (card) => {
    if (card === 'equipment') {
      return equipmentSide
        ? { gridRow: { lg: ROWS[equipmentSide] }, gridColumn: { lg: 2 }, order: { xs: ROWS[equipmentSide] * 10 - 1, lg: 0 } }
        : { gridRow: { lg: 6 }, order: { xs: 60, lg: 0 } };
    }
    return {
      gridRow: { lg: ROWS[card] },
      gridColumn: { lg: card === equipmentSide ? 1 : '1 / -1' },
      order: { xs: ROWS[card] * 10, lg: 0 },
    };
  };

  // Loading state
  if (!currentTask && (loading || loadedTaskId !== taskId)) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress size={48} />
      </Box>
    );
  }

  // Not found state
  if (!currentTask) {
    return (
      <Box>
        <TaskHeader
          title={t('tasks:messages.task_not_found_title')}
          onBack={handleBack}
          showActions={false}
        />
        <Alert severity="error" sx={{ mt: 2 }}>
          {t("tasks:messages.task_not_found")}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with Status and Actions */}
      <TaskHeader
        task={currentTask}
        onBack={handleBack}
        onEdit={canUpdate ? handleEdit : null}
        onCopy={canCreate ? handleCopy : null}
        onStateChange={handleTaskStateChange}
        loading={loading}
        details={
          <TaskFacts
            task={currentTask}
            onAddEquipment={equipmentCount === 0 && canAddEquipment ? () => setAddEquipmentOpen(true) : null}
            onAddManual={manualCount === 0 && canModifyManuals ? () => setAddManualOpen(true) : null}
          />
        }
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* What's needed to do the task. Each card only appears when it has
          content. The equipment checklist sits in a side column next to the
          first of dual signature receipt → description → manuals → procedure
          that the task has; with none of them it takes the full width.
          Placement is CSS only, so the equipment card never remounts when,
          say, manuals finish loading and it moves down. */}
      <Box
        sx={{
          display: 'grid',
          columnGap: 4,
          alignItems: 'start',
          gridTemplateColumns: equipmentSide ? { xs: '1fr', lg: '2fr 1fr' } : '1fr',
        }}
      >
        {/* Dual Signature Completion Receipt Card */}
        {hasReceipt && (
          <TaskCompletionReceiptCard
            task={currentTask}
            onSignatureAdded={loadTaskData}
            onStateChange={canChangeState ? handleTaskStateChange : null}
            loading={loading}
            sx={{ mb: 4, ...place('receipt') }}
          />
        )}

        {/* Adjusted Completion Date Approval Card */}
        {currentTask.completion_approval_id && (
          <TaskCompletionApprovalCard
            task={currentTask}
            onReviewed={loadTaskData}
            sx={{ mb: 4, ...place('approval') }}
          />
        )}

        <TaskInfoCard task={currentTask} sx={{ mb: 4, ...place('info') }} />

        {currentTask.procedure && (
          <TaskProcedureCard
            task={currentTask}
            taskId={taskId}
            onExecutionUpdate={loadTaskData}
            onProcedureCompleted={handleProcedureCompleted}
            sx={{ mb: 4, ...place('procedure') }}
          />
        )}

        <TaskManualsCard
          task={currentTask}
          addOpen={addManualOpen}
          onAddOpen={() => setAddManualOpen(true)}
          onAddClose={() => setAddManualOpen(false)}
          onCountChange={setManualCount}
          sx={{ mb: 4, ...place('manuals') }}
        />

        <TaskEquipmentCard
          task={currentTask}
          onViewEquipment={handleViewEquipment}
          addOpen={addEquipmentOpen}
          onAddOpen={() => setAddEquipmentOpen(true)}
          onAddClose={() => setAddEquipmentOpen(false)}
          onCountChange={setEquipmentCount}
          sx={{ mb: 4, ...place('equipment') }}
        />
      </Box>

      <TaskTabs taskId={taskId} task={currentTask} update_task={loadTaskData} doUpdate={doUpdate} />

      {/* Global Task Actions Dialog */}
      <GlobalTaskActionsDialog
        dialogStates={dialogStates}
        dialogHandlers={dialogHandlers}
        loading={loading}
      />
    </Box>
  );
}